import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { contactName } from "../lib/format";
import type { Contact } from "../lib/types";
import { EmailComposer } from "../components/EmailComposer";
import { ago } from "../lib/format";
import { useIntegrations } from "../lib/integrations";
import { providerName, sendable, useEmail, useSavedEmails, type Mailbox, type SavedEmail } from "../lib/email";
import { Empty, ErrorText, Field, Loading, PageHeader } from "../components/ui";
import { useEntitlements } from "../lib/plans";

const results: Record<string, [string, boolean]> = {
  connected: ["Mailbox connected.", true],
  reconnected: ["Mailbox reconnected.", true],
  cancelled: ["Connecting was cancelled.", false],
  expired: ["That connection link expired or was opened in another browser. Try again from here.", false],
  failed: ["Couldn't connect the mailbox. Try again, or check with your IT team that SVENEX is allowed.", false],
  not_allowed: ["You can't connect or reconnect that mailbox.", false],
  limit: ["Your plan's mailbox limit is reached.", false],
  no_offline_access: ["The mailbox didn't allow SVENEX to stay connected. Try again and accept every permission.", false],
  rate_limited: ["Too many attempts. Wait a minute and try again.", false],
};

const syncLabels = { replies: "Only replies to emails sent from SVENEX", contacts: "Replies, and any email from a known contact", all: "Everything (creates conversations for anyone)" };

export function EmailSettings() {
  const { base, orgId, memberId, allows } = useOrg();
  const qc = useQueryClient();
  const ov = useEmail();
  const integ = useIntegrations();
  const [params, setParams] = useSearchParams();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const result = params.get("email");
  useEffect(() => { if (result) qc.invalidateQueries({ queryKey: [orgId, "email"] }); }, [result, qc, orgId]);
  // Coming back from Microsoft/Google with the browser's Back button restores
  // this page as it was left (buttons still disabled): re-enable them.
  useEffect(() => {
    const shown = (e: PageTransitionEvent) => { if (e.persisted) { setBusy(false); qc.invalidateQueries({ queryKey: [orgId, "email"] }); } };
    window.addEventListener("pageshow", shown);
    return () => window.removeEventListener("pageshow", shown);
  }, [qc, orgId]);
  if (ov.isLoading) return <Loading />;
  if (ov.error) return <ErrorText error={ov.error} />;
  const v0 = ov.data!;
  // Only the mail platforms SVENEX set up for this company.
  const v = { ...v0, providers: v0.providers.filter((p) => integ.has(p === "google" ? "gmail" : "outlook_mail")) };
  const refresh = () => qc.invalidateQueries({ queryKey: [orgId, "email"] });

  async function connect(provider: string, kind: string) {
    setBusy(true); setError(null);
    try {
      const out = await api<{ url: string }>(`${base}/email/connect`, { method: "POST", json: { provider, kind } });
      window.location.assign(out.url);
    } catch (err) { setError(err); setBusy(false); }
  }
  const shared = v.accounts.filter((a) => a.kind === "shared");
  const personal = v.accounts.filter((a) => a.kind === "personal");
  const mine = personal.filter((a) => a.owner_member_id === memberId);

  return (
    <>
      <PageHeader title="Email" />
      {result && results[result] && (
        <p className={results[result][1] ? "notice" : "notice warn"}>{results[result][0]} <button className="link small" onClick={() => setParams({})}>Dismiss</button></p>
      )}
      {!v.enabled_in_plan && <p className="notice small">Email isn't part of your plan yet. Contact SVENEX to add it.</p>}
      <p className="muted">Send from your own Outlook or Gmail inside SVENEX, with AI help. Emails to Outlook mailboxes come into the inbox, and an AI agent can answer them (choose it next to the mailbox). Gmail can send only: reading Gmail needs Google's paid security review.
        {v.sent_limit != null && <> Emails sent this month: <b>{v.sent_this_month.toLocaleString()}</b> of {v.sent_limit.toLocaleString()}.</>}</p>
      {v.providers.length === 0 && <p className="notice small">Email connections aren't set up on this SVENEX server yet.</p>}
      <ErrorText error={error} />
      {v.enabled_in_plan && !allows("readonly") && <WriteEmail canSend={sendable(v, memberId).length > 0} />}

      <section className="card">
        <h2>Company mailboxes</h2>
        <p className="muted small">Shared addresses like bookings@ or info@. Everyone with inbox access can send from them and see their conversations.</p>
        {shared.length === 0 ? <Empty>None connected.</Empty> : shared.map((a) => <MailboxRow key={a.id} a={a} onChange={refresh} onReconnect={() => connect(a.provider, "shared")} canManage={v.can_connect_shared} />)}
        {v.can_connect_shared && v.enabled_in_plan && (
          <div className="inline" style={{ marginTop: ".75rem" }}>
            {v.providers.map((p) => <button key={p} className="secondary" disabled={busy} onClick={() => connect(p, "shared")}>Connect {providerName[p]} company mailbox</button>)}
          </div>
        )}
      </section>

      <section className="card">
        <h2>Your mailbox</h2>
        <p className="muted small">Your own work email. Only you send from it. Conversations that come in are assigned to you (admins and managers can see them too).</p>
        {mine.length === 0 ? <Empty>Not connected.</Empty> : mine.map((a) => <MailboxRow key={a.id} a={a} onChange={refresh} onReconnect={() => connect(a.provider, "personal")} canManage />)}
        {v.enabled_in_plan && (
          <div className="inline" style={{ marginTop: ".75rem" }}>
            {v.providers.map((p) => <button key={p} className="secondary" disabled={busy} onClick={() => connect(p, "personal")}>Connect my {providerName[p]}</button>)}
          </div>
        )}
        {v.providers.includes("google") && <p className="muted small" style={{ marginTop: ".5rem" }}>Gmail is for sending; replies stay in Gmail for now.</p>}
      </section>

      {v.can_connect_shared && personal.filter((a) => a.owner_member_id !== memberId).length > 0 && (
        <section className="card">
          <h2>Team members' mailboxes</h2>
          {personal.filter((a) => a.owner_member_id !== memberId).map((a) => <MailboxRow key={a.id} a={a} onChange={refresh} canManage />)}
        </section>
      )}

      {v.can_connect_shared && <AutomationSender ov={v} onChange={refresh} />}
      <SavedEmails canEdit={v.can_manage} />
    </>
  );
}

function MailboxRow({ a, onChange, onReconnect, canManage }: { a: Mailbox; onChange: () => void; onReconnect?: () => void; canManage: boolean }) {
  const { base, orgId, allows } = useOrg();
  const plan = useEntitlements();
  const canAI = a.can_read && allows("agents") && plan.enabled("website_agent");
  const agents = useQuery({ queryKey: [orgId, "agents"], queryFn: () => api<{ agents: { id: string; name: string; status: string }[] }>(`${base}/agents`), enabled: canManage && canAI });
  const [error, setError] = useState<unknown>(null);
  const patch = async (json: Record<string, unknown>) => {
    setError(null);
    try { await api(`${base}/email/accounts/${a.id}`, { method: "PATCH", json }); onChange(); } catch (err) { setError(err); }
  };
  return (
    <div className="mailbox-row">
      <div>
        <b>{a.email_address}</b> <span className="muted small">{providerName[a.provider]}{a.owner_name && a.kind === "personal" ? ` · ${a.owner_name}` : ""}</span>
        <div className="small">
          {a.status === "active" && <span className="tag status-approved">Connected</span>}
          {a.status === "needs_reconnect" && <span className="tag status-rejected">Needs reconnecting</span>}
          {a.status === "disabled" && <span className="tag status-paused">Paused</span>}
          {a.can_read && a.last_synced_at && <span className="muted"> checked {ago(a.last_synced_at)}</span>}
          {a.ai_agent_id && <span className="kchip ai" style={{ marginLeft: ".4rem" }}>AI replies on</span>}
          {a.last_error && <span className="notice-text"> {a.last_error}</span>}
        </div>
      </div>
      {canManage && (
        <div className="mailbox-actions">
          {a.can_read && (
            <select aria-label="What comes into the inbox" value={a.sync_mode} onChange={(e) => patch({ sync_mode: e.target.value })}>
              {Object.entries(syncLabels).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
          {canAI && (
            <select aria-label="AI replies" value={a.ai_agent_id ?? ""} onChange={(e) => patch(e.target.value ? { ai_agent_id: e.target.value } : { clear_ai_agent: true })}>
              <option value="">AI replies: off</option>
              {(agents.data?.agents ?? []).filter((x) => x.status === "active").map((x) => <option key={x.id} value={x.id}>AI replies: {x.name}</option>)}
            </select>
          )}
          {a.status === "needs_reconnect" && onReconnect && <button className="secondary" onClick={onReconnect}>Reconnect</button>}
          {a.status !== "needs_reconnect" && <button className="secondary" onClick={() => patch({ enabled: a.status === "disabled" })}>{a.status === "disabled" ? "Resume" : "Pause"}</button>}
          <button className="danger" onClick={async () => {
            if (!confirm(`Disconnect ${a.email_address}? Conversations stay in SVENEX; nothing more is sent or received.`)) return;
            try { await api(`${base}/email/accounts/${a.id}`, { method: "DELETE" }); onChange(); } catch (err) { setError(err); }
          }}>Disconnect</button>
        </div>
      )}
      <ErrorText error={error} />
    </div>
  );
}

function AutomationSender({ ov, onChange }: { ov: ReturnType<typeof useEmail>["data"] & object; onChange: () => void }) {
  const { base } = useOrg();
  const [error, setError] = useState<unknown>(null);
  const shared = ov.accounts.filter((a) => a.kind === "shared");
  return (
    <section className="card">
      <h2>Automated emails</h2>
      <p className="muted small">Confirmations and reminders to customers can come from your company mailbox (and show in their email conversation) instead of SVENEX's address.</p>
      <select style={{ maxWidth: 420 }} value={ov.sender_account_id ?? ""} disabled={shared.length === 0} onChange={async (e) => {
        setError(null);
        try { await api(`${base}/email/settings`, { method: "PUT", json: { sender_account_id: e.target.value || null } }); onChange(); } catch (err) { setError(err); }
      }}>
        <option value="">SVENEX's address, with your company name</option>
        {shared.map((a) => <option key={a.id} value={a.id}>{a.email_address}</option>)}
      </select>
      <ErrorText error={error} />
    </section>
  );
}

function SavedEmails({ canEdit }: { canEdit: boolean }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const q = useSavedEmails();
  const [edit, setEdit] = useState<Partial<SavedEmail> | null>(null);
  const [error, setError] = useState<unknown>(null);
  const list = q.data?.templates ?? [];
  async function save() {
    setError(null);
    try {
      const json = { name: edit!.name ?? "", subject: edit!.subject ?? "", body: edit!.body ?? "", subject_ar: edit!.subject_ar ?? "", body_ar: edit!.body_ar ?? "" };
      if (edit!.id) await api(`${base}/email/templates/${edit!.id}`, { method: "PATCH", json });
      else await api(`${base}/email/templates`, { method: "POST", json });
      setEdit(null);
      qc.invalidateQueries({ queryKey: [orgId, "email-templates"] });
    } catch (err) { setError(err); }
  }
  return (
    <section className="card">
      <h2>Saved emails</h2>
      <p className="muted small">Emails your team starts from. {"{{customer_first_name}}"}, {"{{customer_name}}"}, {"{{business}}"} and {"{{my_name}}"} are filled in when sending.</p>
      {list.length === 0 && !edit && <Empty>None yet.</Empty>}
      {!edit && list.map((tp) => (
        <div key={tp.id} className="mailbox-row">
          <div><b>{tp.name}</b> <span className="muted small">{tp.subject || tp.subject_ar}{tp.body_ar ? " · English and Arabic" : ""}</span></div>
          {canEdit && (
            <div className="mailbox-actions">
              <button className="secondary" onClick={() => setEdit(tp)}>Edit</button>
              <button className="danger" onClick={async () => { if (confirm(`Delete "${tp.name}"?`)) { await api(`${base}/email/templates/${tp.id}`, { method: "DELETE" }); qc.invalidateQueries({ queryKey: [orgId, "email-templates"] }); } }}>Delete</button>
            </div>
          )}
        </div>
      ))}
      {edit && (
        <div className="card inset">
          <Field label="Name"><input value={edit.name ?? ""} maxLength={120} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
          <div className="row">
            <Field label="Subject"><input value={edit.subject ?? ""} maxLength={200} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} /></Field>
            <Field label="Subject in Arabic"><input dir="rtl" value={edit.subject_ar ?? ""} maxLength={200} onChange={(e) => setEdit({ ...edit, subject_ar: e.target.value })} /></Field>
          </div>
          <div className="row">
            <Field label="Email"><textarea rows={6} value={edit.body ?? ""} maxLength={10000} onChange={(e) => setEdit({ ...edit, body: e.target.value })} /></Field>
            <Field label="Email in Arabic"><textarea dir="rtl" rows={6} value={edit.body_ar ?? ""} maxLength={10000} onChange={(e) => setEdit({ ...edit, body_ar: e.target.value })} /></Field>
          </div>
          <ErrorText error={error} />
          <div className="inline"><button onClick={save}>Save</button><button className="link" onClick={() => setEdit(null)}>Cancel</button></div>
        </div>
      )}
      {canEdit && !edit && <button className="secondary" style={{ marginTop: ".5rem" }} onClick={() => setEdit({})}>Add a saved email</button>}
    </section>
  );
}

/** WriteEmail starts a new email to a contact: pick who, then the usual composer. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const stageNames: [string, string][] = [["", "Every stage"], ["lead", "Leads"], ["qualified", "Qualified"], ["customer", "Customers"], ["inactive", "Inactive"], ["visitor", "Visitors"]];

/** WriteEmail: one person (a contact, or any address), or a group of contacts. */
function WriteEmail({ canSend }: { canSend: boolean }) {
  const { allows } = useOrg();
  const [mode, setMode] = useState<"" | "one" | "group">("");
  return (
    <section className="card">
      <div className="card-head"><h2>Write an email</h2>
        {mode === "" ? (
          <div className="inline">
            <button disabled={!canSend} onClick={() => setMode("one")}>New email</button>
            {allows("contacts_all") && <button className="secondary" disabled={!canSend} onClick={() => setMode("group")}>Email a group</button>}
          </div>
        ) : (
          <div className="segmented" role="tablist">
            <button role="tab" aria-selected={mode === "one"} className={mode === "one" ? "" : "secondary"} onClick={() => setMode("one")}>One person</button>
            {allows("contacts_all") && <button role="tab" aria-selected={mode === "group"} className={mode === "group" ? "" : "secondary"} onClick={() => setMode("group")}>A group</button>}
          </div>
        )}
      </div>
      {!canSend && <p className="muted small">Connect a mailbox below to start sending.</p>}
      {mode === "one" && <WriteToOne onClose={() => setMode("")} />}
      {mode === "group" && <WriteToGroup onClose={() => setMode("")} />}
    </section>
  );
}

function WriteToOne({ onClose }: { onClose: () => void }) {
  const { base, orgId } = useOrg();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [to, setTo] = useState<{ contact?: Contact; address?: string } | null>(null);
  const search = useQuery({
    queryKey: ["contact-search", base, q],
    queryFn: () => api<{ contacts: Contact[] }>(`${base}/contacts?q=${encodeURIComponent(q.trim())}&limit=8`),
    enabled: !to && q.trim().length >= 2,
  });
  const found = (search.data?.contacts ?? []).filter((c) => c.email);
  const typed = q.trim();
  const isAddress = EMAIL_RE.test(typed);
  if (to) {
    return (
      <div>
        <p className="small to-line">To {to.contact ? <><b>{contactName(to.contact)}</b> <span className="muted">{to.contact.email}</span></> : <b>{to.address}</b>}
          <button className="secondary small" onClick={() => setTo(null)}>Change</button></p>
        <EmailComposer key={to.contact?.id ?? to.address} contactId={to.contact?.id} toAddress={to.address} onCancel={onClose} onSent={(tid) => navigate(`/o/${orgId}/inbox/${tid}`)} />
      </div>
    );
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (isAddress) setTo({ address: typed }); }}>
      <Field label="To"><input autoFocus value={q} maxLength={254} placeholder="Type any email address, or search your contacts" onChange={(e) => setQ(e.target.value)} /></Field>
      <ul className="pick-list">
        {isAddress && !found.some((c) => c.email?.toLowerCase() === typed.toLowerCase()) && (
          <li><button type="submit" className="pick"><b>Email {typed}</b><span className="muted small">Not one of your contacts</span></button></li>
        )}
        {found.map((c) => (
          <li key={c.id}><button type="button" className="pick" onClick={() => setTo({ contact: c })}><b>{contactName(c)}</b><span className="muted small">{c.email}</span></button></li>
        ))}
      </ul>
      {search.isFetching && <Loading rows={2} />}
      {typed.length >= 2 && !isAddress && search.isSuccess && found.length === 0 && <p className="muted small">No contacts with an email match. Type the full address to email someone new.</p>}
      <button type="button" className="link" onClick={onClose}>Cancel</button>
    </form>
  );
}

interface GroupCount { recipients: number; no_email: number; opted_out: number; not_opted_in: number; over_limit: boolean }

function WriteToGroup({ onClose }: { onClose: () => void }) {
  const { base } = useOrg();
  const [who, setWho] = useState<"all" | "pick">("all");
  const [stage, setStage] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [picked, setPicked] = useState<Contact[]>([]);
  const [q, setQ] = useState("");
  const [done, setDone] = useState<number | null>(null);
  const search = useQuery({
    queryKey: ["contact-search", base, q],
    queryFn: () => api<{ contacts: Contact[] }>(`${base}/contacts?q=${encodeURIComponent(q.trim())}&limit=8`),
    enabled: who === "pick" && q.trim().length >= 2,
  });
  const audience = who === "all" ? { all: true, ...(stage ? { stage } : {}), marketing } : { contact_ids: picked.map((c) => c.id), marketing };
  const ready = who === "all" || picked.length > 0;
  const preview = useQuery({
    queryKey: ["email-group-preview", base, JSON.stringify(audience)],
    queryFn: () => api<{ count: GroupCount; max: number }>(`${base}/email/groups/preview`, { method: "POST", json: audience }),
    enabled: ready,
  });
  const c = preview.data?.count;
  if (done != null) {
    return (
      <div className="notice">Sent to {done.toLocaleString()} {done === 1 ? "person" : "people"}. Each one has their own conversation in the inbox, and replies come back there.
        <div className="inline" style={{ marginTop: ".6rem" }}><button className="secondary small" onClick={() => setDone(null)}>Write another</button><button className="secondary small" onClick={onClose}>Close</button></div></div>
    );
  }
  return (
    <div className="group-email">
      <div className="row">
        <Field label="Send to">
          <select value={who} onChange={(e) => setWho(e.target.value as "all" | "pick")}>
            <option value="all">All contacts</option>
            <option value="pick">Contacts I choose</option>
          </select>
        </Field>
        {who === "all" && (
          <Field label="Stage">
            <select value={stage} onChange={(e) => setStage(e.target.value)}>{stageNames.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </Field>
        )}
      </div>
      {who === "pick" && (
        <>
          <Field label="Add contacts"><input value={q} maxLength={100} placeholder="Search by name or email" onChange={(e) => setQ(e.target.value)} /></Field>
          <ul className="pick-list">
            {(search.data?.contacts ?? []).filter((x) => x.email && !picked.some((p) => p.id === x.id)).map((x) => (
              <li key={x.id}><button type="button" className="pick" onClick={() => { setPicked([...picked, x]); setQ(""); }}><b>{contactName(x)}</b><span className="muted small">{x.email}</span></button></li>
            ))}
          </ul>
          {picked.length > 0 && (
            <div className="picked">{picked.map((x) => (
              <span key={x.id} className="picked-chip">{contactName(x)}<button type="button" aria-label={`Remove ${contactName(x)}`} onClick={() => setPicked(picked.filter((p) => p.id !== x.id))}>×</button></span>
            ))}</div>
          )}
        </>
      )}
      <label className="check"><input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} /> This is marketing (offers, promotions, newsletters): send only to contacts who agreed to marketing emails</label>
      {ready && c && (
        <p className={`group-count${c.over_limit ? " warn" : ""}`}>
          <b>{c.recipients.toLocaleString()}</b> {c.recipients === 1 ? "person gets" : "people get"} this email
          {(c.no_email > 0 || c.opted_out > 0 || c.not_opted_in > 0) && <span className="muted"> · left out: {[
            c.no_email > 0 && `${c.no_email} without an email`, c.opted_out > 0 && `${c.opted_out} opted out`, c.not_opted_in > 0 && `${c.not_opted_in} not opted in to marketing`,
          ].filter(Boolean).join(", ")}</span>}
          {c.over_limit && <span> · That's over {preview.data!.max} people: choose a stage or pick contacts.</span>}
        </p>
      )}
      {ready && c && c.recipients > 0 && !c.over_limit && (
        <EmailComposer key={JSON.stringify(audience)} group={audience} onCancel={onClose} onGroupSent={(n) => setDone(n)} />
      )}
      {!ready && <p className="muted small">Add at least one contact.</p>}
      <p className="muted small">Each person gets their own email, with their name filled in where you write {"{{customer_first_name}}"}. Nobody sees who else got it.</p>
    </div>
  );
}
